import { ColDef, ICellRendererParams, IRichCellEditorParams, SetFilterParams } from 'ag-grid-community';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBulkValidateReportsMutation, useUpdateReportMutation } from '../../../../hooks/mutations/useReportMutation';
import { useReportsQuery } from '../../../../hooks/queries/useReportsQuery';
import { useTrucksQuery } from '../../../../hooks/queries/useTrucksQuery';
import { useUtilityVehiclesQuery } from '../../../../hooks/queries/useUtilityVehiclesQuery';
import { useAuth } from '../../../../hooks/useAuth';
import { useSystemConfig } from '../../../../hooks/useSystemConfig';
import { DataItem, Report } from '../../../../services/apiSLTP/activities';
import { capitalizeFirstLetter, formatName } from '../../../../utils/capitalize';
import { getReportStateColor, getReportTypeColor } from '../../../../utils/colors';
import { formatDate } from '../../../../utils/dates';
import { getReportStateInfos, getReportTypeInfos, permissionLevels, reportStateEnum, reportTypeEnum } from '../../../../utils/enums';
import StateCell from '../../../cells/StateCell';
import Icon from '../../../Icon';
import IconLine from '../../../IconLine';
import CreateAbsentReportModal from '../../../modals/operations/planning/CreateAbsentReportModal';
import CreateAbsentsReportsModal from '../../../modals/operations/planning/CreateAbsentsReportsModal';
import GenericAgGridTable from '../../GenericAgGridTable';
import IconButton from '../../../shared/IconButton';

interface ReportsPlanningTableProps {
    currentDate: Date;
    refetch: () => void;
    activities: DataItem[];
}

const ReportsPlanningTable: React.FC<ReportsPlanningTableProps> = ({ activities, currentDate, refetch }) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const { getConfigValue } = useSystemConfig();

    const {
        data: dataReports,
        isLoading: isReportsLoading,
        refetch: refetchReports,
    } = useReportsQuery({ queries: { date: currentDate.toISOString() } });

    const { mutate: validateReports, isLoading: isValidateReportsLoading } = useBulkValidateReportsMutation({
        onSettled: () => {
            refetch();
        },
    });

    const { mutate: mutateReport, isLoading: isMutateReportLoading } = useUpdateReportMutation({
        onSettled: () => {
            refetch();
            refetchReports();
        },
    });

    const { data: dataTrucks, isLoading: isTrucksLoading } = useTrucksQuery({ queries: { active: true } });
    const { data: dataVehicles, isLoading: isVehiclesLoading } = useUtilityVehiclesQuery({ queries: { active: true } });

    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showCreateRangeModal, setShowCreateRangeModal] = useState(false);

    const sortActivitiesByPerson = () => {
        return (dataReports || [])
            .filter((value) => !value.userId.isSpecial)
            .sort((a, b) => {
                if (formatName(a.userId.firstName, a.userId.lastName) > formatName(b.userId.firstName, b.userId.lastName)) return 1;
                if (formatName(a.userId.firstName, a.userId.lastName) < formatName(b.userId.firstName, b.userId.lastName)) return -1;
                return 0;
            });
    };

    const columnDefs: ColDef<Report>[] = useMemo(
        () => [
            {
                headerName: capitalizeFirstLetter(t('name')),
                valueGetter: (params) => formatName(params.data?.userId.firstName, params.data?.userId.lastName),
                colId: 'name',
                initialFlex: 1,
                minWidth: 200,
            },
            {
                headerName: capitalizeFirstLetter(t('vehicle')),
                colId: 'vehicle',
                field: 'vehicleId',
                valueFormatter: (params) => (params.value ? params.value.designation : ''),
                cellEditor: 'agRichSelectCellEditor',
                cellEditorParams: {
                    values: (dataVehicles || []).sort((a, b) => a.designation.localeCompare(b.designation)),
                    formatValue: (activity) => (activity ? activity.designation : ''),
                    valueListMaxHeight: 500,
                } as IRichCellEditorParams,
                keyCreator: (params) => params.value?._id,
                onCellValueChanged: async (params) => {
                    const newVehicle = params.newValue;
                    if (newVehicle?._id === params.oldValue?._id) return;
                    mutateReport({ id: params.data._id, body: { vehicleId: newVehicle?._id || null, vehiclePrice: null, vehiclePriceType: null } });
                },
                editable: (params) => !!params.data && params.data.reportState >= reportStateEnum.PLANNING,
            },
            {
                headerName: capitalizeFirstLetter(t('truck')),
                colId: 'truck',
                field: 'truckId',
                valueFormatter: (params) => (params.value ? params.value.designation : ''),
                cellEditor: 'agRichSelectCellEditor',
                cellEditorParams: {
                    values: (dataTrucks || [])
                        .filter((value) => value.truckCompanyId.isOwnCompany)
                        .sort((a, b) => a.designation.localeCompare(b.designation)),
                    formatValue: (truck) => (truck ? truck.designation : ''),
                    valueListMaxHeight: 500,
                } as IRichCellEditorParams,
                keyCreator: (params) => params.value?._id,
                onCellValueChanged: async (params) => {
                    const newTruck = params.newValue;
                    if (newTruck?._id === params.oldValue?._id) return;
                    mutateReport({ id: params.data._id, body: { truckId: newTruck?._id || null, truckPrice: null, truckPriceType: null } });
                },
                editable: (params) => !!params.data && params.data.reportState >= reportStateEnum.PLANNING,
            },
            {
                headerName: capitalizeFirstLetter(t('state')),
                cellRenderer: (params: ICellRendererParams<Report>) => {
                    if (!params.data) return params.value;
                    return (
                        <StateCell stateColor={getReportTypeColor(params.data?.reportType)} text={t(getReportTypeInfos(params.data?.reportType))} />
                    );
                },
                colId: 'reportType',
                field: 'reportType',
                valueFormatter: (params) => (params.value ? capitalizeFirstLetter(t(getReportTypeInfos(params.value))) : ''),
                cellEditor: 'agRichSelectCellEditor',
                cellEditorParams: {
                    values: Object.values(reportTypeEnum),
                    formatValue: (type) => (type ? capitalizeFirstLetter(t(getReportTypeInfos(type))) : ''),
                    valueListMaxHeight: 500,
                } as IRichCellEditorParams,
                onCellValueChanged: async (params) => {
                    const newType = params.newValue;
                    if (!newType || newType === params.oldValue) return;
                    mutateReport({ id: params.data._id, body: { reportType: newType } });
                },
                filterParams: {
                    valueFormatter: (params) => capitalizeFirstLetter(t(getReportTypeInfos(params.value))),
                } as SetFilterParams,
                editable: (params) => !!params.data && params.data.reportState >= reportStateEnum.PLANNING,
            },
            {
                headerName: capitalizeFirstLetter(t('status')),
                valueGetter: (params) => capitalizeFirstLetter(t(getReportStateInfos(params.data?.reportState))),
                cellRenderer: (params: ICellRendererParams<Report>) => {
                    if (!params.data) return params.value;
                    return (
                        <StateCell
                            stateColor={getReportStateColor(params.data?.reportState)}
                            text={t(getReportStateInfos(params.data?.reportState))}
                        />
                    );
                },
                colId: 'reportState',
            },
            {
                headerName: t('nbActivities'),
                colId: 'nbActivities',
                valueGetter: (params) => {
                    const reportId = params.data?._id;
                    return activities.filter((a) => a.reportId._id === reportId).length;
                },
            },
            {
                headerName: capitalizeFirstLetter(t('actions')),
                sortable: false,
                filter: false,
                enableRowGroup: false,
                enableValue: false,
                cellRenderer: (params: ICellRendererParams<Report>) => {
                    if (!params.data) return null;
                    return (
                        <>
                            {!!user &&
                                user?.profileId.permsLevel <= permissionLevels.PLANNING &&
                                params.data?.reportType === reportTypeEnum.ABSENT && (
                                    <IconLine>
                                        {params.data?.reportState === reportStateEnum.REPORTFILLED && (
                                            <IconButton
                                                iconName="resetTv"
                                                onClick={() =>
                                                    mutateReport({ id: params.data?._id || '', body: { reportState: reportStateEnum.PLANNING } })
                                                }
                                                title={capitalizeFirstLetter(t('takeBackReport'))}
                                                loading={isMutateReportLoading}
                                            />
                                        )}
                                        {params.data?.reportState > reportStateEnum.REPORTFILLED && (
                                            <IconButton
                                                iconName="verticalAlignTop"
                                                onClick={() =>
                                                    mutateReport({ id: params.data?._id || '', body: { reportState: reportStateEnum.REPORTFILLED } })
                                                }
                                                title={capitalizeFirstLetter(t('validateReport'))}
                                                loading={isMutateReportLoading}
                                            />
                                        )}
                                    </IconLine>
                                )}
                        </>
                    );
                },
                colId: 'actions',
            },
            ...((user?.profileId.permsLevel === permissionLevels.ADMIN
                ? [
                      {
                          headerName: capitalizeFirstLetter(t('_id')),
                          field: '_id',
                          colId: '_id',
                          initialWidth: 250,
                          cellClass: 'usage-col-main',
                          headerClass: 'usage-col-main',
                      },
                  ]
                : []) as ColDef<Report>[]),
        ],
        [dataReports, dataVehicles, dataTrucks, activities]
    );

    return (
        <>
            <GenericAgGridTable
                rowData={sortActivitiesByPerson()}
                columnDefs={columnDefs}
                exportCsvFileName={`planning-reports-${formatDate(currentDate)}`}
                agGridProps={{
                    getRowId: ({ data }) => data._id,
                }}
                headerRightComponent={
                    !!user &&
                    user?.profileId.permsLevel <= permissionLevels.PLANNING && (
                        <>
                            <IconButton
                                iconName="addCircle"
                                onClick={() => setShowCreateModal(true)}
                                title={capitalizeFirstLetter(t('addAbsence'))}
                            />
                            <IconButton
                                iconName="playListAddCircle"
                                onClick={() => setShowCreateRangeModal(true)}
                                title={capitalizeFirstLetter(t('addAbsences'))}
                            />
                        </>
                    )
                }
            />
            {showCreateModal && (
                <CreateAbsentReportModal
                    currentDate={currentDate}
                    handleClose={() => setShowCreateModal(false)}
                    open={showCreateModal}
                    refetch={() => {
                        refetchReports();
                        refetch();
                    }}
                    reports={dataReports || []}
                    activities={activities}
                />
            )}
            {showCreateRangeModal && (
                <CreateAbsentsReportsModal
                    currentDate={currentDate}
                    handleClose={() => setShowCreateRangeModal(false)}
                    open={showCreateRangeModal}
                    refetch={() => {
                        refetchReports();
                        refetch();
                    }}
                    reports={dataReports || []}
                />
            )}
        </>
    );
};

export default ReportsPlanningTable;
